// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useEffect, useCallback, useRef } from 'react'
import { ChevronLeft, Loader2 } from 'lucide-react'
import { formatUsDate } from '../../api/format'
import SearchableSelect from '../../components/common/SearchableSelect'
import LabPanel from '../../components/carerx/LabPanel'
import * as api from '../../api/client'
import TherapyDocumentButtons from './TherapyDocumentButtons'
import { friendlyError } from '../../api/errors'
import { resultOptionsFor } from '../../lib/labResults'
import CareRxCarePlanPanels from './CareRxCarePlanPanels'
import CarePlanAssignee from './CarePlanAssignee'
import CareRxScreeningPanel from './CareRxScreeningPanel'
import TherapyRefTag from '../../components/ui/TherapyRefTag'
import PatientLanguageBadge from '../../components/ui/PatientLanguageBadge'
import EnrollmentReviewNotes from './EnrollmentReviewNotes'
import DrugAssessmentCard from './DrugAssessmentCard'
import { CrStatusSelect, CrDeterminationControl } from '../../components/carerx/ClinicalReviewDecision'
import DateInput from '../../components/ui/DateInput'
import Notice from '../../components/ui/Notice'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { useToast } from '../../components/ui/Toast'

// D-CRX-45 Clinical Review dial-in (Phase 3): enrollment snapshot (Pioneer-locked), lab results
// entry (CR-E2), 12-month confirm (CR-E3), re-draw send-back (CR-E4), and the CR-V5 screening
// prompt (result matches a rule → move to Screening & Hold y/n, No requires a reason).
// The CR-E9 Consultation + CR-E10 Care Plan panels are wired in Phase 5.

type Detail = Awaited<ReturnType<typeof api.getClinicalReviewDetail>>
type Draft = { value_text: string; value_num: string; pos_neg: string; comments: string; draw_date: string; results_date: string; within_12mo: boolean }
/** ASP 2026-10-28 (#8): the banner's colour comes from an explicit kind — it used to be guessed from the
 *  text ("has a '.' and no 'error'"), so "Something went wrong. Please try again." showed as a success. */
export type PageMsg = { text: string; kind: 'success' | 'error' }

/**
 * ASP 2026-10-28 (#8): a refresh reseeds lab drafts from the server, EXCEPT rows edited since their
 * last save — a single-row Save must not wipe what the pharmacist typed in the other rows.
 */
export function mergeLabDrafts(server: Record<string, Draft>, prev: Record<string, Draft>, unsaved: ReadonlySet<string>): Record<string, Draft> {
  const out: Record<string, Draft> = { ...server }
  for (const a of unsaved) if (prev[a]) out[a] = prev[a]
  return out
}

const lock = (): React.CSSProperties => ({ fontSize: 9.5, color: '#6b7787', background: '#eef1f4', borderRadius: 4, padding: '1px 5px', marginLeft: 6 })
const fld: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3 }
const lab: React.CSSProperties = { fontSize: 10.5, letterSpacing: '.04em', textTransform: 'uppercase', color: '#8496a5', fontWeight: 700 }
const inp: React.CSSProperties = { fontSize: 12.5, padding: '5px 8px', border: '1px solid #dde5ec', borderRadius: 6, background: '#fff' }
const locked: React.CSSProperties = { ...inp, background: '#eef1f4', color: '#41566a' }

// backLabel: the enrollment record is reachable from Review, Renewals and Hold, so the way out
// names the stage you actually came from instead of always claiming Clinical Review.
export default function CareRxClinicalReviewDetail({ therapyId, onBack, backLabel = 'Back to Clinical Review',
  showScreening = true, alreadyOnHold = false, showDecision = false, readOnly = false }:
  // showScreening — the D-CRX-71 rule panel belongs to the REVIEW stage. Hold runs its own
  //   (lab-driven) rule set and does not need a second set of questions on the same record.
  // alreadyOnHold — the record is being read FROM Hold. "Send to Screening & Hold" is then an
  //   offer to do what has already happened, so it is not shown.
  // showDecision — Status + Determination controls. Only the Review stage records the outcome;
  //   Renewals and Hold open this record read-only for those two fields, as before.
  // readOnly (ASP 2026-10-11 #15) — a DECIDED review opened from the archive or from Consent & Drug
  //   Order Form: a record, not a worklist item. Every control below the header is disabled, so an
  //   approved (dispensing) therapy cannot be re-drawn, re-screened or sent to Hold from here.
  { therapyId: string; onBack: () => void; backLabel?: string; showScreening?: boolean; alreadyOnHold?: boolean; showDecision?: boolean; readOnly?: boolean }) {
  const [data, setData] = useState<Detail | null>(null)
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState<PageMsg | null>(null)
  const toast = useToast()
  const showError = (e: unknown) => setMsg({ text: friendlyError(e), kind: 'error' })
  /** A success that clears itself — unless something newer has replaced it by then. */
  const flash = (text: string, ms: number) => {
    const m: PageMsg = { text, kind: 'success' }
    setMsg(m); setTimeout(() => setMsg(cur => (cur === m ? null : cur)), ms)
  }
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  /** Per-analyte edit counter; an analyte is "unsaved" while it has an entry (see mergeLabDrafts). */
  const editSeq = useRef(new Map<string, number>())
  /** After a successful post, forget the rows it carried — unless they were edited again meanwhile. */
  const markSaved = (analytes: string[], seqAtPost: Map<string, number>) => {
    for (const a of analytes) if (editSeq.current.get(a) === seqAtPost.get(a)) editSeq.current.delete(a)
  }
  // #8: the consultation / care-plan panels hold unsaved text in local state; Back asks first.
  const [panelsDirty, setPanelsDirty] = useState(false)
  const [leaveOpen, setLeaveOpen] = useState(false)
  const requestBack = () => { if (panelsDirty) setLeaveOpen(true); else onBack() }
  const [prompt, setPrompt] = useState<{ analyte: string; rule: api.CrMatchedRule } | null>(null)
  /**
   * D-CRX-80: the analytes a BULK save came back positive on. The single-save path raises the
   * CR-V5 screening prompt one analyte at a time, which cannot work for a panel saved together —
   * the pharmacist would meet the second positive only after dismissing the first. Listed instead,
   * so every one is visible at once and none is dismissed unseen.
   */
  const [bulkPositives, setBulkPositives] = useState<string[]>([])
  /** Save-all in flight — disables the button so a double click cannot post the panel twice. */
  const [savingAll, setSavingAll] = useState(false)
  const [overrideReason, setOverrideReason] = useState('')
  const [showOverride, setShowOverride] = useState(false)
  // D-CRX-66 manual forward: the CR-V5 prompt only appears when a POSITIVE result matches a rule.
  // This is the same destination reached on the pharmacist's own judgement — any analyte, any
  // result, or none at all — so the labs panel always has a door to Screening & Hold.
  const [sendOpen, setSendOpen] = useState(false)
  const [sendReason, setSendReason] = useState('')
  const [sendAnalyte, setSendAnalyte] = useState('')
  const [sendRule, setSendRule] = useState('')
  const [sendBusy, setSendBusy] = useState(false)
  const [sentNote, setSentNote] = useState<string | null>(null)
  const [rules, setRules] = useState<api.ScreeningRule[]>([])
  useEffect(() => { api.getScreeningRules().then(r => setRules(r.rules ?? [])).catch(() => setRules([])) }, [])
  const [redrawFor, setRedrawFor] = useState<string | null>(null)
  const [redraw, setRedraw] = useState({ reason: '', redraw_on: '' })

  // ASP 2026-10-28 (#8): `loading` no longer swaps the whole page for a spinner after the first load.
  // It did, which UNMOUNTED the care-plan panels on every lab save; their unsaved consultation note and
  // goals live only in local state, so the remount refetched the last saved version and the typed care
  // plan was gone. Refreshes now keep the page mounted.
  const load = useCallback(() => {
    setLoading(true)
    return api.getClinicalReviewDetail(therapyId)
      .then(d => {
        setData(d)
        // seed drafts from saved results
        const seed: Record<string, Draft> = {}
        const analytes = new Set<string>([...d.required_analytes, ...d.results.map(r => r.analyte)])
        for (const a of analytes) {
          const r = d.results.find(x => x.analyte === a)
          seed[a] = {
            value_text: r?.value_text ?? '', value_num: r?.value_num != null ? String(r.value_num) : '',
            pos_neg: r?.pos_neg ?? '', comments: r?.comments ?? '',
            draw_date: r?.draw_date ? String(r.draw_date).slice(0, 10) : '',
            results_date: r?.results_date ? String(r.results_date).slice(0, 10) : '',
            within_12mo: r?.within_12mo ?? false,
          }
        }
        setDrafts(prev => mergeLabDrafts(seed, prev, new Set(editSeq.current.keys())))
      })
      .catch(e => setMsg({ text: friendlyError(e), kind: 'error' }))
      .finally(() => setLoading(false))
  }, [therapyId])
  // A different therapy is a first load: nothing of the previous record (data, unsaved rows) carries over.
  useEffect(() => { editSeq.current.clear(); setData(null) }, [therapyId])
  useEffect(() => { load() }, [load])

  // After a Status/Determination write, re-read the therapy header ONLY. load() would reseed the lab
  // drafts from the server and silently drop any results the pharmacist has typed but not saved.
  const [decisionErr, setDecisionErr] = useState('')
  const decisionSaved = () => {
    setDecisionErr('')
    api.getClinicalReviewDetail(therapyId)
      .then(d => setData(prev => prev ? { ...prev, therapy: d.therapy } : d))
      .catch(e => setDecisionErr(friendlyError(e)))
  }

  const setDraft = (analyte: string, patch: Partial<Draft>) => {
    editSeq.current.set(analyte, (editSeq.current.get(analyte) ?? 0) + 1)
    setDrafts(d => ({ ...d, [analyte]: { ...d[analyte], ...patch } }))
  }

  const saveResult = async (analyte: string) => {
    const d = drafts[analyte]; if (!d) return
    const seq = new Map(editSeq.current)
    try {
      const res = await api.saveClinicalReviewLabResult(therapyId, {
        analyte, value_num: d.value_num ? Number(d.value_num) : null, value_text: d.value_text || undefined,
        pos_neg: d.pos_neg || undefined, comments: d.comments || undefined,
        draw_date: d.draw_date || undefined, results_date: d.results_date || undefined, within_12mo: d.within_12mo,
      })
      if (res.matched_rule) { setPrompt({ analyte, rule: res.matched_rule }); setShowOverride(false); setOverrideReason('') }
      else flash('Saved.', 1500)
      markSaved([analyte], seq)
      load()
    } catch (e) { showError(e) }
  }

  const sendToScreening = async () => {
    if (!sendReason.trim()) return
    setSendBusy(true); setMsg(null)
    try {
      const rule = rules.find(r => r.rule_ref === sendRule)
      await api.clinicalReviewScreenDecision(therapyId, {
        move_to_screening: true, manual: true, reason: sendReason.trim(),
        rule_ref: sendRule || undefined, analyte: sendAnalyte || undefined,
        trigger: rule?.trigger ?? undefined, hold_decision: rule?.hold_decision ?? undefined,
      })
      setSendOpen(false); setSendReason(''); setSendAnalyte(''); setSendRule('')
      setSentNote('Sent to Screening & Hold. It is now on that tab; resolving the hold returns this RX to Clinical review.')
      load()
    } catch (e) { showError(e) } finally { setSendBusy(false) }
  }

  const decideScreening = async (move: boolean) => {
    if (!prompt) return
    if (!move && !overrideReason.trim()) { setShowOverride(true); return }
    try {
      await api.clinicalReviewScreenDecision(therapyId, {
        rule_ref: prompt.rule.rule_ref, analyte: prompt.analyte, trigger: prompt.rule.trigger ?? undefined,
        hold_decision: prompt.rule.hold_decision ?? undefined, move_to_screening: move,
        override_reason: move ? undefined : overrideReason,
      })
      setPrompt(null); setShowOverride(false); setOverrideReason('')
      flash(move ? 'Moved to Screening & Hold.' : 'Override recorded.', 2000)
      load()
    } catch (e) { showError(e) }
  }

  const sendRedraw = async (analyte: string) => {
    if (!redraw.reason.trim()) return
    try {
      await api.clinicalReviewRedraw(therapyId, { analyte, reason: redraw.reason, redraw_on: redraw.redraw_on || undefined })
      setRedrawFor(null); setRedraw({ reason: '', redraw_on: '' })
      flash('Sent back to Lab Tracker for re-draw.', 2500)
      load()
    } catch (e) { showError(e) }
  }

  if (loading && !data) return <div style={{ padding: 30, textAlign: 'center', color: '#8496a5' }}><Loader2 className="spin" size={20} /> Loading…</div>
  if (!data) return <div style={{ padding: 20 }}><button onClick={onBack}>← Back</button><div style={{ color: '#b23a48' }}>{msg?.text}</div></div>
  // Locked only when the review IS decided: opened read-only from Consent & Drug Order Form, an
  // undecided review stays workable (board 2026-10-12: never call a pending review "decided").
  const decidedLock = readOnly && (data.therapy.determination === 'approved' || data.therapy.determination === 'denied')
  const t = data.therapy
  // Review L15: the server matches analytes case-insensitively ("HBsAg" = "hbsag"); a case-sensitive
  // lookup here offered normal-range answers the server then refused for an infection screen.
  const screeningAnalytes = new Set((data.screening_analytes ?? []).map(a => a.trim().toLowerCase()))
  const analytes = Object.keys(drafts).sort()

  return (
    <div>
      <button onClick={requestBack} style={{ fontSize: 12, color: '#185FA5', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3, border: 'none', background: 'none' }}>
        <ChevronLeft size={15} /> {backLabel}
      </button>
      {loading && <span role="status" style={{ marginLeft: 10, fontSize: 11.5, color: '#8496a5', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Loader2 className="spin" size={12} /> Refreshing…</span>}
      <ConfirmDialog open={leaveOpen} tone="danger" title="Leave without saving?"
        body="The consultation or care plan has unsaved changes. Leaving now discards them."
        confirmLabel="Leave without saving" cancelLabel="Stay"
        onConfirm={() => { setLeaveOpen(false); onBack() }} onCancel={() => setLeaveOpen(false)} />

      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 660 }}>{t.patient_name}<PatientLanguageBadge patientId={t.patient_id} /> · {t.drug_name} · <span style={{ fontFamily: 'ui-monospace' }}>{t.rx_number}</span></h2>
          {showDecision ? (<>
            <div style={{ fontSize: 12, color: '#8496a5' }}><TherapyRefTag therapyId={therapyId} />{t.care_plan_type ? ` · care plan type ${t.care_plan_type}` : ''}</div>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
              <div style={fld}><label style={lab}>Status</label>
                <CrStatusSelect therapyId={therapyId} value={t.cr_status} onSaved={decisionSaved} onError={setDecisionErr} /></div>
              <div style={fld}><label style={lab}>Determination</label>
                <CrDeterminationControl therapyId={therapyId} value={t.determination} onSaved={decisionSaved} onError={setDecisionErr} /></div>
            </div>
            {decisionErr && <div style={{ fontSize: 12, color: '#b23a48', marginTop: 6 }}>{decisionErr}</div>}
          </>) : (
            <>
            <div style={{ fontSize: 12, color: '#8496a5' }}><TherapyRefTag therapyId={therapyId} /> · {t.care_plan_type ? `care plan type ${t.care_plan_type}` : ''} · determination {t.determination} · status {t.cr_status}</div>
            {/* ASP 2026-10-11 (#15): a denied review read from the archive says why. */}
            {t.determination === 'denied' && t.denial_reason && <div style={{ fontSize: 12, color: '#b23a48', marginTop: 4 }}>Denied: {t.denial_reason}</div>}
            </>
          )}
        </div>
        {/* D-CRX-63: the reviewer decides whether this therapy proceeds, so this screen offers the
            two documents that decision is actually about — the enrollment form and the consent.
            The D-CRX-62 "Review PDF" (the clinical-review DETERMINATION SHEET, kind
            'clinical_review') was removed here on Mario's direction 2026-08-27: it is the
            reviewer's own decision record, not a patient document, and it read as though it were
            the review's copy of the enrollment. NOTE the consequence — this was the only UI caller
            of generateCareRxStepDoc('clinical-review'), so that artifact is once again generatable
            only through the API. Already-generated copies remain on the patient's chart. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <TherapyDocumentButtons therapyId={therapyId}
            buttonStyle={{ fontSize: 12, padding: '6px 12px', border: '1px solid #dde5ec', borderRadius: 8, background: '#fff', cursor: 'pointer' }}
            onError={m => setMsg({ text: m, kind: 'error' })} />
        </div>
      </div>

      {msg && <div style={{ marginTop: 10 }}><Notice severity={msg.kind}>{msg.text}</Notice></div>}

      {/* Decided: the outcome, labs, screening and care plan are locked; a review note can still be
          added (notes only add, dated and audited), and the documents still open. */}
      {decidedLock && <div style={{ fontSize: 12, color: '#41566a', background: '#f3f6f9', border: '1px solid #dde5ec', borderRadius: 8, padding: '7px 11px', marginTop: 12 }}>
        Read-only — this review is decided. Labs, screening and the care plan are locked; you can still add a review note and open the documents.</div>}
      {/* Enrollment snapshot — Pioneer-locked fields */}
      <section style={{ border: '1px solid #dde5ec', borderRadius: 12, padding: 15, marginTop: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 660, marginBottom: 10 }}>Enrollment snapshot</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12 }}>
          <div style={fld}><label style={lab}>Patient <span style={lock()}>Pioneer</span></label><input value={t.patient_name ?? ''} disabled style={locked} /></div>
          <div style={fld}><label style={lab}>DOB <span style={lock()}>Pioneer</span></label><input value={t.dob ? formatUsDate(t.dob) : ''} disabled style={locked} /></div>
          <div style={fld}><label style={lab}>City <span style={lock()}>Pioneer</span></label><input value={t.city ?? ''} disabled style={locked} /></div>
          <div style={fld}><label style={lab}>Allergies <span style={lock()}>Pioneer</span></label><input value={t.allergies ?? ''} disabled style={locked} /></div>
          {/* Patrick 2026-10-08: the ICD-10 and its source. Codes read from the prescriber's e-script note
              are not PioneerRx-coded, so they say so and ask to be verified. */}
          <div style={fld}><label style={lab}>ICD-10 {data.diagnosis?.source === 'escript_note'
              ? <span title="PioneerRx sent no coded diagnosis; read from the prescriber's e-script note"
                  style={{ ...lock(), color: 'var(--color-warning-fg, #92400e)', background: 'var(--color-warning-bg, #fef3c7)' }}>E-script note — verify</span>
              : data.diagnosis?.source === 'enrollment' ? <span style={lock()}>Enrollment</span>
              : data.diagnosis ? <span style={lock()}>Pioneer</span> : null}</label>
            <input value={data.diagnosis?.codes ?? ''} disabled style={locked} />
            {/* ASP 2026-10-11 (#10): the diagnosis by name, from the official ICD-10-CM code set. */}
            {data.diagnosis?.names && <div style={{ fontSize: 11.5, color: '#41566a', marginTop: 3 }}>{data.diagnosis.names}</div>}</div>
          <div style={fld}><label style={lab}>Dose / SIG</label><input value={t.dose_sig ?? ''} disabled style={locked} /></div>
          <div style={fld}><label style={lab}>Next infusion</label><input value={t.next_infusion ? formatUsDate(t.next_infusion) : ''} disabled style={locked} /></div>
        </div>
      </section>

      {/* ASP #33: the notes a pharmacist needs while reviewing — the clinical notes typed at
          enrollment, any screening-override justifications, and the enrollment's review-notes
          thread (newest first, paged on the server; adding one here is the same note as on Enrollment). */}
      <section style={{ border: '1px solid #dde5ec', borderRadius: 12, padding: 15, marginTop: 14 }} aria-label="Enrollment notes">
        <div style={{ fontSize: 13, fontWeight: 660, marginBottom: 10 }}>Enrollment notes</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#8496a5', marginBottom: 3 }}>Clinical notes (from enrollment)</div>
        <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', marginBottom: 12 }}>{t.clinical_notes?.trim() || <span style={{ color: '#8496a5' }}>None recorded.</span>}</div>
        {t.intake_notes?.trim() && (<>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#8496a5', marginBottom: 3 }}>Screening overrides</div>
          <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap', marginBottom: 12 }}>{t.intake_notes}</div>
        </>)}
        <EnrollmentReviewNotes key={t.patient_id} patientId={t.patient_id} editHint={false} />
      </section>

      {/* D-CRX-71 screening questions. ABOVE the labs, deliberately: they are a step of the review
          every therapy passes through, not something a lab result triggers. Sitting under the Labs
          card read as "these are about the labs", and a pharmacist with no labs to enter scrolled
          past them. They gate the determination; they belong where the review starts. */}
      {/* A disabled fieldset disables every native control inside it, child panels included. */}
      {showScreening && <CareRxScreeningPanel key={`screening-${therapyId}`} therapyId={therapyId} readOnly={decidedLock} />}
      {/* #28: the pharmacist stays reassignable on a decided review, so its picker sits outside the locked fieldset. */}
      {decidedLock && <div style={{ marginTop: 12 }}><CarePlanAssignee therapyId={therapyId} /></div>}
      <fieldset disabled={decidedLock} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {/* ASP #13: the drug's own questions, written on its drug record, answered here. */}
      <DrugAssessmentCard key={therapyId} therapyId={therapyId} />

      {/* Lab results entry */}
      <section style={{ border: '1px solid #dde5ec', borderRadius: 12, padding: 15, marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 660, marginBottom: 4 }}>Labs — enter results &amp; confirm dates</div>
            <div style={{ fontSize: 11.5, color: '#8496a5', marginBottom: 10 }}>{alreadyOnHold
              ? 'This RX is already on Screening & Hold — resolve the hold to return it to Clinical review.'
              : 'A positive result matching a screening rule prompts to move the RX to Screening & Hold (CR-V5) — or send it yourself at any time.'}</div>
          </div>
          {/* Already on Hold: offering to send it there is an action with nothing to do. */}
          {!alreadyOnHold && !decidedLock && (
            <button onClick={() => { setSendOpen(v => !v); setSentNote(null) }}
              style={{ ...inp, cursor: 'pointer', fontWeight: 600, color: '#92610f', borderColor: '#f0c000', background: sendOpen ? '#fffbeb' : '#fff', whiteSpace: 'nowrap' }}>
              Send to Screening &amp; Hold
            </button>)}
        </div>
        {sentNote && (
          <div style={{ fontSize: 12, color: '#0d6b52', background: '#e1f4ec', border: '1px solid #9ad3bd', borderRadius: 8, padding: '7px 11px', marginBottom: 10 }}>{sentNote}</div>
        )}
        {sendOpen && (
          <div style={{ border: '1px solid #f0c000', background: '#fffbeb', borderRadius: 9, padding: 13, marginBottom: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#92610f' }}>Send this RX to Screening &amp; Hold</div>
            <div style={{ fontSize: 11.5, color: '#41566a', margin: '4px 0 9px' }}>
              Your clinical judgement — no rule match needed. The reason travels with the hold, so whoever
              picks it up on the Screening &amp; Hold tab can see why it is there.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={sendReason} onChange={e => setSendReason(e.target.value)} autoFocus
                placeholder="Reason (required)" style={{ ...inp, width: 340 }} />
              {/* Both lists are data-bound (the therapy's analytes, the pharmacy's rule set), so
                  they are typeahead selects rather than raw HTML dropdowns — E-rules / Gate 19. */}
              <div style={{ minWidth: 210 }}>
                <SearchableSelect value={sendAnalyte} onChange={(v: string) => setSendAnalyte(v)} ariaLabel="Related analyte"
                  options={[{ value: '', label: 'Related analyte (optional)' }, ...analytes.map(a => ({ value: a, label: a }))]} />
              </div>
              <div style={{ minWidth: 300 }}>
                <SearchableSelect value={sendRule} onChange={(v: string) => setSendRule(v)} ariaLabel="Screening rule"
                  options={[{ value: '', label: 'Screening rule (optional)' },
                            ...rules.filter(r => r.active).map(r => ({ value: r.rule_ref, label: `${r.rule_ref} — ${r.condition}` }))]} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 9, flexWrap: 'wrap' }}>
              <button disabled={sendBusy || !sendReason.trim()} onClick={sendToScreening}
                style={{ ...inp, cursor: sendReason.trim() ? 'pointer' : 'not-allowed', background: '#d97706', color: '#fff', border: 'none', fontWeight: 600, opacity: sendReason.trim() ? 1 : .5 }}>
                {sendBusy ? 'Sending…' : 'Confirm — send to Screening & Hold'}
              </button>
              <button onClick={() => { setSendOpen(false); setSendReason('') }} style={{ ...inp, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}
        {analytes.length === 0 ? <div style={{ color: '#8496a5', fontSize: 12.5 }}>No required labs recorded for this therapy.</div> : (
          <div style={{ overflowX: 'auto' }}>
            {/* D-CRX-80: the shared LabPanel. This screen owns carerx_lab_results — the pharmacist's
                record of what the result SAID, as against the tracker's record that a draw HAPPENED
                — and it had no bulk edit, so a twenty-analyte panel was twenty round trips, each a
                chance to be interrupted and leave a therapy part-resulted. Save-per-row stays as a
                rowAction; the bulk bar posts them together through /lab-results/bulk. */}
            {/* A bulk save can return several positives at once. The per-row CR-V5 prompt shows one
                analyte at a time, so listing them here is what stops the second and third being
                dismissed unseen along with the first. */}
            {bulkPositives.length > 0 && (
              <div style={{ border: '1px solid #b23a48', background: '#fdf1f1', color: '#7f1d1d',
                            borderRadius: 8, padding: '8px 11px', marginBottom: 10, fontSize: 12.5 }}>
                <b>{bulkPositives.length} positive result{bulkPositives.length === 1 ? '' : 's'} saved:</b>{' '}
                {bulkPositives.join(', ')} — review each for Screening &amp; Hold.
                <button onClick={() => setBulkPositives([])}
                  style={{ ...inp, cursor: 'pointer', marginLeft: 10, padding: '2px 8px' }}>Dismiss</button>
              </div>
            )}
            <LabPanel
              rows={analytes.map(a => {
                const d = drafts[a]
                return {
                  analyte: a,
                  value_num: d?.value_num ?? '',
                  value_text: d?.value_text ?? '',
                  result: d?.pos_neg ?? '',
                  comments: d?.comments ?? '',
                  collected_on: d?.draw_date ?? '',
                  results_on: d?.results_date ?? '',
                  // ≤12mo is COMPUTED from the results date, never asked (owner 2026-08-13) — a
                  // manual checkbox invited stale attestations. Same rule the server applies.
                  current: d?.results_date
                    ? new Date(d.results_date + 'T00:00:00') >= new Date(Date.now() - 365 * 864e5)
                    : undefined,
                }
              })}
              editable={{ collectedOn: true, resultsOn: true, result: true, value: true, comments: true }}
              resultOptions={row => resultOptionsFor(screeningAnalytes.has((row.analyte ?? '').trim().toLowerCase()))}
              onEdit={(row, patch) => setDraft(row.analyte, {
                ...(patch.value_num !== undefined ? { value_num: patch.value_num ?? '' } : {}),
                ...(patch.value_text !== undefined ? { value_text: patch.value_text ?? '' } : {}),
                ...(patch.result !== undefined ? { pos_neg: patch.result ?? '' } : {}),
                ...(patch.comments !== undefined ? { comments: patch.comments ?? '' } : {}),
                ...(patch.collected_on !== undefined ? { draw_date: patch.collected_on ?? '' } : {}),
                ...(patch.results_on !== undefined ? { results_date: patch.results_on ?? '' } : {}),
              })}
              onBulk={async (rows, patch) => {
                // Apply to the drafts first so the screen reflects the change even if the post
                // fails, then persist them together — one transaction, all or nothing.
                for (const row of rows)
                  setDraft(row.analyte, {
                    ...(patch.result !== undefined ? { pos_neg: patch.result ?? '' } : {}),
                    ...(patch.collected_on !== undefined ? { draw_date: patch.collected_on ?? '' } : {}),
                    ...(patch.results_on !== undefined ? { results_date: patch.results_on ?? '' } : {}),
                  })
                try {
                  const res = await api.saveCrLabResultsBulk(therapyId, rows.map(row => {
                    const d = drafts[row.analyte]
                    return {
                      analyte: row.analyte,
                      value_num: d?.value_num ? Number(d.value_num) : null,
                      value_text: d?.value_text || undefined,
                      pos_neg: (patch.result ?? d?.pos_neg) || undefined,
                      comments: d?.comments || undefined,
                      draw_date: (patch.collected_on ?? d?.draw_date) || undefined,
                      results_date: (patch.results_on ?? d?.results_date) || undefined,
                    }
                  }))
                  // Every positive at once, rather than the pharmacist meeting them one row at a time.
                  if (res.positives?.length) setBulkPositives(res.positives)
                  // What was posted is what was saved: these rows are no longer unsaved drafts.
                  for (const row of rows) editSeq.current.delete(row.analyte)
                  await load()
                } catch (e) {
                  // #8: the page's toast kit, not a window alert().
                  toast.show(friendlyError(e, 'Bulk save failed.'), 'error')
                }
              }}
              saveAllLabel="Save all results"
              onSaveAll={async rows => {
                // Every analyte the pharmacist has typed, written in ONE transaction. Previously
                // this panel had a Save per row and nothing else: a twenty-analyte set meant twenty
                // requests, and being interrupted half way left the therapy part-resulted — which
                // reads downstream as an incomplete review rather than an interrupted one.
                setSavingAll(true)
                const seq = new Map(editSeq.current)
                try {
                  const res = await api.saveCrLabResultsBulk(therapyId, rows.map(row => {
                    const d = drafts[row.analyte]
                    return {
                      analyte: row.analyte,
                      value_num: d?.value_num ? Number(d.value_num) : null,
                      value_text: d?.value_text || undefined,
                      pos_neg: d?.pos_neg || undefined,
                      comments: d?.comments || undefined,
                      draw_date: d?.draw_date || undefined,
                      results_date: d?.results_date || undefined,
                    }
                  }))
                  if (res.positives?.length) setBulkPositives(res.positives)
                  markSaved(rows.map(r => r.analyte), seq)
                  await load()
                } catch (e) {
                  toast.show(friendlyError(e, 'Save all failed.'), 'error')
                } finally { setSavingAll(false) }
              }}
              busy={savingAll}
              rowActions={row => (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => saveResult(row.analyte)} style={{ ...inp, cursor: 'pointer', background: '#185FA5', color: '#fff', border: 'none', fontWeight: 600 }}>Save</button>
                  <button onClick={() => { setRedrawFor(row.analyte); setRedraw({ reason: '', redraw_on: '' }) }} style={{ ...inp, cursor: 'pointer' }}>Re-draw</button>
                </div>
              )}
              emptyText="No analytes required for this therapy."
            />
          </div>
        )}

        {/* Re-draw send-back */}
        {redrawFor && (
          <div style={{ marginTop: 10, border: '1px solid #dde5ec', borderRadius: 9, padding: 12, background: '#f7f9fb' }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>Send {redrawFor} back to Lab Tracker for re-draw</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input placeholder="Reason (required)" value={redraw.reason} onChange={e => setRedraw(r => ({ ...r, reason: e.target.value }))} style={{ ...inp, width: 260 }} />
              <DateInput label="Re-draw on" value={redraw.redraw_on} onChange={v => setRedraw(r => ({ ...r, redraw_on: v ?? '' }))} style={{ ...inp, width: 140 }} />
              <button onClick={() => sendRedraw(redrawFor)} style={{ ...inp, cursor: 'pointer', background: '#d97706', color: '#fff', border: 'none', fontWeight: 600 }}>Send to re-draw</button>
              <button onClick={() => setRedrawFor(null)} style={{ ...inp, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        {/* CR-V5 screening prompt */}
        {prompt && (
          <div style={{ marginTop: 12, border: '1px solid #f0c000', background: '#fffbeb', borderRadius: 9, padding: 13 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#92610f' }}>⚠ Result matches a Screening &amp; Hold rule (CR-V5)</div>
            <div style={{ fontSize: 11.5, color: '#41566a', margin: '4px 0 9px' }}>
              {prompt.analyte} <b>positive</b> matches rule <b>{prompt.rule.rule_ref} — {prompt.rule.condition}</b>{prompt.rule.hold_decision ? `: ${prompt.rule.hold_decision}` : ''}. Move this RX to Screening &amp; Hold?
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => decideScreening(true)} style={{ ...inp, cursor: 'pointer', background: '#d97706', color: '#fff', border: 'none', fontWeight: 600 }}>Yes — move to Screening &amp; Hold</button>
              <button onClick={() => setShowOverride(true)} style={{ ...inp, cursor: 'pointer' }}>No</button>
            </div>
            {showOverride && (
              <div style={{ display: 'flex', gap: 8, marginTop: 9, alignItems: 'center', flexWrap: 'wrap' }}>
                <input placeholder="Override reason (required)" value={overrideReason} onChange={e => setOverrideReason(e.target.value)} style={{ ...inp, width: 300 }} />
                <button onClick={() => decideScreening(false)} style={{ ...inp, cursor: 'pointer', background: '#dc2626', color: '#fff', border: 'none', fontWeight: 600 }}>Confirm override</button>
                <span style={{ fontSize: 11, color: '#8496a5' }}>No ⇒ reason required (CR-V5).</span>
              </div>
            )}
          </div>
        )}
      </section>

      {/* D-CRX-71 non-lab screening renders ONCE, above the Labs card — see the mount near the top
          of this component. It used to be mounted here as well: two independent instances of the
          same panel, each fetching and rendering the full rule set, so a pharmacist saw every
          screening question twice and an "N unresolved" count twice. Both mounts carried a comment
          explaining why that position was the right one, which is how the second survived review.
          The panel gates the determination, so its position is a real decision — it is made once,
          up there. */}

      {/* CR-E9 Consultation + CR-E10 Care Plan panels (Phase 5) */}
      <CareRxCarePlanPanels therapyId={therapyId} consultationNeeded={t.consultation_needed ?? null} onDirtyChange={setPanelsDirty} hideAssignee={decidedLock} />
      </fieldset>
    </div>
  )
}
